/** @jsxRuntime automatic */
/** @jsxImportSource react */
import type { CSSProperties, ReactNode, ElementType } from "react";
import { parseMarkdownToHtml } from "./markdown.ts";
import type {
  BlockProduct,
  CartContentsOptions,
  CollectionListingOptions,
  ProductDetailOptions,
  ProductCustomFieldOptions,
  BlockRenderArgs,
  RenderContext,
  SlotRender,
  GalleryViewerImage,
} from "./types.ts";
import { layoutHasAspectRatio, layoutStyle, slideRadiusStyle } from "./layout.ts";
import { bricksElementStyle, bricksIconToneClass, normalizeLengthValue, slotBasisVars } from "./bricks-layout.ts";
import { mediaImage, responsiveImgAttrs } from "./media-image.ts";
import { PLACEHOLDER_IMAGE_URL } from "./media.ts";
import { SvgIcon } from "./svg-icon.tsx";
import type * as R from "./registry.ts";
import { ProductDetailSample } from "./views-product.tsx";
import { MobileMenu, NavDropdownCloser } from "./views-mobile-menu-client.tsx";
import { BgVideo } from "./views-bg-video-client.tsx";
import type { LogoSize } from "./common.ts";
import { NewsletterForm } from "./newsletter-form.tsx";

/**
 * Presentational renderers for every block. Pure: no hooks, no data fetching, no browser
 * APIs, so they render identically in the storefront (server) and the visual editor.
 * All styling is class-based (blocks.css); merchants only pick enumerated options.
 */

const cx = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(" ");
type A<P> = BlockRenderArgs<P>;

const mediaSrc = (ctx: RenderContext | undefined, id: string | undefined): string | null =>
  id ? (ctx?.mediaUrl?.(id) ?? null) : null;

/**
 * The first background picture drawn in a page tree is the likely largest paint, so it loads eagerly with high priority;
 * every later one stays lazy. The claim lives on the per-tree context, so each render of a tree starts fresh.
 */
const claimLcp = (ctx: RenderContext | undefined): boolean => {
  const claim = ctx?.lcpClaim;
  if (!claim || claim.taken) return false;
  claim.taken = true;
  return true;
};
const imgLoading = (eager: boolean) =>
  eager ? ({ loading: "eager", fetchPriority: "high", decoding: "async" } as const) : ({ loading: "lazy" } as const);

/** Logo picture: the 320 / 640px derivative for a logo uploaded at full size (it is drawn well under 300px wide). */
const logoAttrs = (ctx: RenderContext | undefined, mediaId: string | undefined, fallback: string) => {
  const r = responsiveImgAttrs(ctx, mediaId, "240px", fallback);
  return { src: r.src ?? fallback, ...(r.srcSet ? { srcSet: r.srcSet, sizes: r.sizes } : null) };
};

const isExternal = (href: string) => /^https?:\/\//i.test(href);
const rel = (href: string, newTab?: boolean) =>
  newTab || isExternal(href) ? "noopener noreferrer" : undefined;

/** Storefront prices are minor units of the shopper's market currency (ADR-040); INR with en-IN when the host gives no market. */
const money = (minor: number, ctx?: RenderContext) => {
  const currency = ctx?.currency ?? "INR";
  const major = minor / (currency === "JPY" ? 1 : 100);
  return new Intl.NumberFormat(ctx?.locale ?? (currency === "INR" ? "en-IN" : "en-US"), {
    style: "currency",
    currency,
    minimumFractionDigits: major % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(major);
};

function Stars({ value }: { value: number }) {
  const v = Math.max(0, Math.min(5, Math.round(value)));
  return (
    <span className="bsb-stars" role="img" aria-label={`${v} out of 5 stars`}>
      {"★".repeat(v)}
      {"☆".repeat(5 - v)}
    </span>
  );
}

/* ---------------------------- sections ---------------------------- */

function SectionHead({
  title,
  subtitle,
  align = "left",
  action,
}: {
  title?: string | undefined;
  subtitle?: string | undefined;
  align?: "left" | "center" | "right";
  action?: ReactNode;
}) {
  if (!title && !subtitle) return null;
  return (
    <div className={cx("bsb-section-head", `bsb-align-${align}`)}>
      <div>
        {title ? <h2 className="bsb-heading bsb-h-lg">{title}</h2> : null}
        {subtitle ? <p className="bsb-muted" style={{ margin: "0.4rem 0 0" }}>{subtitle}</p> : null}
      </div>
      {action}
    </div>
  );
}

export function BannerView({ props, ctx }: A<R.BannerProps>): ReactNode {
  const all = [{ text: props.text, link: props.link }, ...props.messages];
  const rotating = props.rotate && all.length > 1;
  const style = {
    padding: "0.6rem 1rem",
    fontSize: `${props.fontSize / 16}rem`,
    fontWeight: Number(props.fontWeight),
    ...(props.font === "heading" ? { fontFamily: "var(--fh, inherit)" } : null),
    ...(props.bgColor ? { background: props.bgColor } : null),
    ...(props.textColor ? { color: props.textColor } : null),
    "--ann-n": all.length,
    "--ann-iv": `${props.interval}s`,
  } as CSSProperties;
  const bar = (
    <div
      className={cx("bsb bs-block-banner bsb-ann", !props.bgColor && "bsb-tone-primary", `bsb-align-${props.align}`, rotating && `bsb-ann-rotate bsb-ann-n${all.length}`)}
      style={style}
      role="region"
      aria-label="Announcements"
    >
      {rotating ? (
        <button type="button" className="bsb-ann-nav bsb-ann-prev" data-ann-dir="-1" aria-label="Previous announcement">
          ‹
        </button>
      ) : null}
      {all.map((m, i) => (
        <p key={i} className="bsb-ann-msg" style={{ "--ann-i": i } as CSSProperties} {...(rotating && i > 0 ? { "aria-hidden": true } : null)}>
          {m.link ? (
            // A linked message is not underlined: a small arrow after the text shows that it can be clicked.
            <a href={m.link} className="bsb-ann-link">
              {m.text}
              <span className="bsb-ann-go" aria-hidden="true">
                →
              </span>
            </a>
          ) : (
            m.text
          )}
        </p>
      ))}
      {rotating ? (
        <button type="button" className="bsb-ann-nav bsb-ann-next" data-ann-dir="1" aria-label="Next announcement">
          ›
        </button>
      ) : null}
    </div>
  );
  return rotating && ctx?.renderBanner ? <>{ctx.renderBanner({ children: bar })}</> : bar;
}

export function CollectionGridView({ props, ctx }: A<R.CollectionGridProps>): ReactNode {
  const d = ctx?.blockId ? ctx.data?.[ctx.blockId] : undefined;
  const collections = d && d.kind === "collections" ? d.collections : [];
  return (
    <section className="bsb bs-block-collection-grid bsb-py-lg">
      <div className="bsb-w bsb-w-wide">
        <SectionHead title={props.title} subtitle={props.subtitle} align={props.headerAlign} />
        {collections.length === 0 ? (
          <p className={cx("bsb-muted", `bsb-align-${props.headerAlign}`)}>No collections selected yet.</p>
        ) : (
          <div className="bsb-grid bsb-ccards" style={{ "--cols": Number(props.columns), "--cols-t": Number(props.columnsTablet), "--cols-m": Number(props.columnsMobile) } as CSSProperties}>
            {collections.map((c) => {
              const imgAttrs = responsiveImgAttrs(ctx, c.imageMediaId, `(max-width: 767px) ${Math.round(100 / Number(props.columnsMobile))}vw, (max-width: 1023px) ${Math.round(100 / Number(props.columnsTablet))}vw, ${Math.round(100 / Number(props.columns))}vw`);
              const img = imgAttrs.src;
              return (
                <a key={c.slug} className="bsb-ccard" href={`/${props.source === "categories" ? "categories" : "collections"}/${c.slug}`}>
                  <div className="bsb-ccard-media">
                    <img src={img ?? PLACEHOLDER_IMAGE_URL} {...(img && imgAttrs.srcSet ? { srcSet: imgAttrs.srcSet, sizes: imgAttrs.sizes } : null)} alt="" loading="lazy" />
                  </div>
                  <div className="bsb-ccard-body">
                    <h3 className="bsb-ccard-title">{c.title}</h3>
                    {c.description ? <p className="bsb-ccard-desc">{c.description}</p> : null}
                  </div>
                </a>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

export function ReviewsView({ props }: A<R.ReviewsProps>): ReactNode {
  return (
    <section className="bsb bs-block-reviews bsb-py-lg">
      <div className="bsb-w bsb-w-default">
        <SectionHead title={props.title} />
      </div>
    </section>
  );
}

export function GalleryView({ props, ctx }: A<R.GalleryProps>): ReactNode {
  const viewer: GalleryViewerImage[] = [];
  const items = props.images.map((im, i) => {
    const detail = im.mediaId ? ctx?.mediaDetails?.(im.mediaId) : null;
    const src = detail?.url ?? mediaSrc(ctx, im.mediaId);
    if (!src) return null;
    const imgInfo = mediaImage(
      detail
        ? { url: src, width: detail.width, height: detail.height, focalX: detail.focalX, focalY: detail.focalY, dominantColor: detail.dominantColor, derivatives: detail.derivatives }
        : { url: src },
      { sizes: `(max-width: 640px) ${100 / Number(props.columnsMobile)}vw, (max-width: 1023px) ${100 / Number(props.columnsTablet)}vw, ${100 / Number(props.columnsDesktop)}vw` },
    );
    const alt = im.caption ?? detail?.alt ?? "";
    const openable = props.lightbox && !im.link;
    const index = viewer.length;
    if (openable) viewer.push({ src, thumb: imgInfo.src, alt, caption: im.caption });
    const img = (
      <img
        className="bsb-gal-img"
        src={imgInfo.src}
        srcSet={imgInfo.srcSet}
        sizes={imgInfo.sizes}
        alt={alt}
        loading="lazy"
        style={{ objectPosition: imgInfo.objectPosition, backgroundColor: imgInfo.dominantColor ?? undefined }}
      />
    );
    return (
      <figure key={i} className="bsb-gal-item" {...(openable ? { "data-gal-i": index, role: "button", tabIndex: 0, "aria-label": alt ? `Open ${alt}` : "Open image" } : null)}>
        {im.link ? <a href={im.link}>{img}</a> : img}
      </figure>
    );
  });
  // A gallery with no pictures draws nothing on the storefront (not even its heading); the editors draw a hint so it can still be found.
  const hasPictures = items.some(Boolean);
  if (!hasPictures && ctx?.renderGallery) return null;
  const grid = (
    <div
      className={cx("bsb-gal", `bsb-gal-${props.layout}`, props.heightMode === "manual" && "bsb-gal-fixed", props.lightbox && "bsb-gal-open")}
      style={{ "--gal-d": Number(props.columnsDesktop), "--gal-t": Number(props.columnsTablet), "--gal-m": Number(props.columnsMobile), "--gal-gap": `${props.gap}px`, "--gal-rad": `${props.radius}px`, "--gal-h": `${props.manualHeight}px` } as CSSProperties}
    >
      {hasPictures ? items : <p className="bsb-muted bsb-align-center" style={{ gridColumn: "1 / -1", margin: 0 }}>Add pictures to this gallery.</p>}
    </div>
  );
  return (
    <section className="bsb bs-block-gallery bsb-py-lg">
      <div className="bsb-w bsb-w-wide">
        {props.title ? <h2 className={`bsb-heading bsb-h-lg bsb-align-${props.headerAlign}`} style={{ margin: 0 }}>{props.title}</h2> : null}
        {props.description ? <p className={`bsb-muted bsb-align-${props.headerAlign}`} style={{ margin: `${props.title ? props.titleGap : 0}px ${props.headerAlign === "center" ? "auto" : "0"} 0 ${props.headerAlign === "right" ? "auto" : props.headerAlign === "center" ? "auto" : "0"}`, maxWidth: "40rem" }}>{props.description}</p> : null}
        {props.title || props.description ? <div aria-hidden="true" style={{ height: props.headerGap }} /> : null}
        {props.lightbox && viewer.length > 0 && ctx?.renderGallery ? <>{ctx.renderGallery({ images: viewer, children: grid })}</> : grid}
      </div>
    </section>
  );
}


/* ----------------------------- layout ----------------------------- */

/** Renders a layout block's children: a plain wrapper on the storefront, the drop zone in the editor. */
function Slot({
  children,
  className,
  style,
}: {
  children: ReactNode | SlotRender | undefined;
  className?: string;
  style?: CSSProperties;
}) {
  if (typeof children === "function") return <>{children({ className, style } as { className?: string; style?: CSSProperties })}</>;
  return (
    <div className={className} style={style}>
      {children}
    </div>
  );
}

export function SectionView({ props, children, ctx }: A<R.SectionProps>): ReactNode {
  const bgImageId = props.background?.mediaId || props.backgroundMediaId;
  const desktopBg = props.background?.imageUrl || mediaSrc(ctx, bgImageId);
  const bgResp = props.background?.imageUrl ? null : responsiveImgAttrs(ctx, bgImageId, "100vw");
  const tabletBg = props.responsive?.tablet?.background?.imageUrl || mediaSrc(ctx, props.responsive?.tablet?.background?.mediaId);
  const mobileBg = props.responsive?.mobile?.background?.imageUrl || mediaSrc(ctx, props.responsive?.mobile?.background?.mediaId);
  const bg = desktopBg || tabletBg || mobileBg;

  const bgVideo = props.background?.videoUrl;
  const videoPoster = mediaSrc(ctx, props.background?.videoPosterMediaId) || mediaSrc(ctx, props.background?.videoPosterId) || desktopBg || mobileBg || tabletBg;
  const customStyle = bricksElementStyle(props, { defaultWidth: "100%", defaultDisplay: "flex" });

  const hasExplicitBg = Boolean(bg || bgVideo || props.background?.color);

  return (
    <section
      className={cx("bsb brxe-section brx-resp", bricksIconToneClass(props), !hasExplicitBg && props.tone && `bsb-tone-${props.tone}`, props.paddingY && `bsb-py-${props.paddingY}`)}
      style={{
        position: "relative",
        boxSizing: "border-box",
        ...customStyle,
      }}
    >
      {bg ? (
        <>
          <picture style={{ position: "absolute", inset: 0, width: "100%", height: "100%", zIndex: 0, borderRadius: "inherit", overflow: "hidden" }}>
            {mobileBg && <source media="(max-width: 767px)" srcSet={mobileBg} />}
            {tabletBg && <source media="(max-width: 1023px)" srcSet={tabletBg} />}
            <img
              className="bsb-hero-bg"
              src={desktopBg || tabletBg || mobileBg || ""}
              alt=""
              {...(bgResp?.srcSet ? { srcSet: bgResp.srcSet, sizes: bgResp.sizes } : null)}
              {...imgLoading(claimLcp(ctx))}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: (props.background?.imageSize as CSSProperties["objectFit"]) || "cover",
                objectPosition: props.background?.imagePosition || "center center",
                zIndex: 0,
                borderRadius: "inherit",
              }}
            />
          </picture>
          {props.overlayOpacity ? <div className="bsb-hero-scrim" style={{ position: "absolute", inset: 0, background: "#000", opacity: props.overlayOpacity / 100, zIndex: 0, borderRadius: "inherit" }} /> : null}
        </>
      ) : null}
      {bgVideo ? (
        <BgVideo
          src={bgVideo}
          poster={videoPoster ?? undefined}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: (props.background?.videoFit as CSSProperties["objectFit"]) || "cover",
            objectPosition: props.background?.videoPosition || "center center",
            zIndex: 0,
            borderRadius: "inherit",
          }}
        />
      ) : null}
      <Slot
        style={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          display: "flex",
          flexDirection: (props.direction ? `var(--b-dir, ${props.direction})` : "var(--b-dir, column)") as CSSProperties["flexDirection"],
          ...slotBasisVars(props),
          alignItems: (props.alignCrossAxis ? `var(--b-ai, ${props.alignCrossAxis})` : "var(--b-ai, stretch)") as CSSProperties["alignItems"],
          justifyContent: (props.alignMainAxis ? `var(--b-jc, ${props.alignMainAxis})` : "var(--b-jc, flex-start)") as CSSProperties["justifyContent"],
          gap: props.gap ? `var(--b-gap, ${props.gap})` : (props.responsive?.tablet?.gap || props.responsive?.mobile?.gap ? "var(--b-gap)" : undefined),
          rowGap: props.rowGap ? `var(--b-rg, ${props.rowGap})` : (props.responsive?.tablet?.rowGap || props.responsive?.mobile?.rowGap ? "var(--b-rg)" : undefined),
          columnGap: props.colGap ? `var(--b-cg, ${props.colGap})` : (props.responsive?.tablet?.colGap || props.responsive?.mobile?.colGap ? "var(--b-cg)" : undefined),
        }}
      >
        {children}
      </Slot>
    </section>
  );
}

export function ContainerView({ props, children, ctx }: A<R.ContainerProps>): ReactNode {
  const desktopBg = props.background?.imageUrl || mediaSrc(ctx, props.background?.mediaId);
  const bgResp = props.background?.imageUrl ? null : responsiveImgAttrs(ctx, props.background?.mediaId, "100vw");
  const tabletBg = props.responsive?.tablet?.background?.imageUrl || mediaSrc(ctx, props.responsive?.tablet?.background?.mediaId);
  const mobileBg = props.responsive?.mobile?.background?.imageUrl || mediaSrc(ctx, props.responsive?.mobile?.background?.mediaId);
  const bg = desktopBg || tabletBg || mobileBg;

  const bgVideo = props.background?.videoUrl;
  const videoPoster = mediaSrc(ctx, props.background?.videoPosterMediaId) || mediaSrc(ctx, props.background?.videoPosterId) || desktopBg || mobileBg || tabletBg;
  const customStyle = bricksElementStyle(props, { defaultWidth: "100%", defaultDisplay: "flex" });

  const hasExplicitBg = Boolean(bg || bgVideo || props.background?.color);
  const hasCustomTone = props.tone && props.tone !== "default";

  return (
    <div
      className={cx(
        "bsb brxe-container brx-resp",
        bricksIconToneClass(props),
        !hasExplicitBg && hasCustomTone && `bsb-tone-${props.tone}`,
        props.paddingY && props.paddingY !== "none" && `bsb-py-${props.paddingY}`,
        props.align && `bsb-align-${props.align}`
      )}
      style={{
        position: "relative",
        zIndex: 1,
        boxSizing: "border-box",
        ...customStyle,
      }}
    >
      {bg ? (
        <picture style={{ position: "absolute", inset: 0, width: "100%", height: "100%", zIndex: 0, borderRadius: "inherit", overflow: "hidden" }}>
          {mobileBg && <source media="(max-width: 767px)" srcSet={mobileBg} />}
          {tabletBg && <source media="(max-width: 1023px)" srcSet={tabletBg} />}
          <img
            className="bsb-hero-bg"
            src={desktopBg || tabletBg || mobileBg || ""}
            alt=""
            {...(bgResp?.srcSet ? { srcSet: bgResp.srcSet, sizes: bgResp.sizes } : null)}
            {...imgLoading(claimLcp(ctx))}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: (props.background?.imageSize as CSSProperties["objectFit"]) || "cover",
              objectPosition: props.background?.imagePosition || "center center",
              zIndex: 0,
              borderRadius: "inherit",
            }}
          />
        </picture>
      ) : null}
      {bgVideo ? (
        <BgVideo
          src={bgVideo}
          poster={videoPoster ?? undefined}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: (props.background?.videoFit as CSSProperties["objectFit"]) || "cover",
            objectPosition: props.background?.videoPosition || "center center",
            zIndex: 0,
            borderRadius: "inherit",
          }}
        />
      ) : null}
      <Slot
        style={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: (props.direction ? `var(--b-dir, ${props.direction})` : "var(--b-dir, column)") as CSSProperties["flexDirection"],
          ...slotBasisVars(props),
          alignItems: (props.alignCrossAxis ? `var(--b-ai, ${props.alignCrossAxis})` : "var(--b-ai, stretch)") as CSSProperties["alignItems"],
          justifyContent: (props.alignMainAxis ? `var(--b-jc, ${props.alignMainAxis})` : "var(--b-jc, flex-start)") as CSSProperties["justifyContent"],
          gap: props.gap ? `var(--b-gap, ${props.gap})` : (props.responsive?.tablet?.gap || props.responsive?.mobile?.gap ? "var(--b-gap)" : undefined),
          rowGap: props.rowGap ? `var(--b-rg, ${props.rowGap})` : (props.responsive?.tablet?.rowGap || props.responsive?.mobile?.rowGap ? "var(--b-rg)" : undefined),
          columnGap: props.colGap ? `var(--b-cg, ${props.colGap})` : (props.responsive?.tablet?.colGap || props.responsive?.mobile?.colGap ? "var(--b-cg)" : undefined),
          flexWrap: (props.flexWrap ? `var(--b-wrap, ${props.flexWrap})` : "var(--b-wrap, nowrap)") as CSSProperties["flexWrap"],
        }}
      >
        {children}
      </Slot>
    </div>
  );
}

export function BlockView({ props, children, ctx }: A<R.BlockProps>): ReactNode {
  const desktopBg = props.background?.imageUrl || mediaSrc(ctx, props.background?.mediaId);
  const bgResp = props.background?.imageUrl ? null : responsiveImgAttrs(ctx, props.background?.mediaId, "100vw");
  const tabletBg = props.responsive?.tablet?.background?.imageUrl || mediaSrc(ctx, props.responsive?.tablet?.background?.mediaId);
  const mobileBg = props.responsive?.mobile?.background?.imageUrl || mediaSrc(ctx, props.responsive?.mobile?.background?.mediaId);
  const bg = desktopBg || tabletBg || mobileBg;

  const bgVideo = props.background?.videoUrl;
  const videoPoster = mediaSrc(ctx, props.background?.videoPosterMediaId) || mediaSrc(ctx, props.background?.videoPosterId) || desktopBg || mobileBg || tabletBg;
  
  // If custom width is given, use it; otherwise use flex: 1 1 0 with minWidth: 0 so sibling blocks distribute space automatically
  const hasCustomWidth = Boolean(props.sizing?.width);
  const customStyle = bricksElementStyle(props, {
    defaultWidth: hasCustomWidth ? undefined : "auto",
    defaultDisplay: "flex",
  }) as CSSProperties & Record<string, string | number | undefined>;
  if (hasCustomWidth) {
    // A width set for desktop only means "this column"; when the row stacks on tablet/mobile it should fill the line
    // unless that device sets its own width. The width is a per-device variable (not flex-basis) so it follows the device.
    const tabletW = normalizeLengthValue(props.responsive?.tablet?.sizing?.width) ?? "100%";
    customStyle["--brx-t-w"] = tabletW;
    customStyle["--brx-m-w"] = normalizeLengthValue(props.responsive?.mobile?.sizing?.width) ?? tabletW;
  }

  return (
    <div
      className={cx("bsb brxe-block brx-resp", bricksIconToneClass(props))}
      style={{
        position: "relative",
        zIndex: 1,
        boxSizing: "border-box",
        // Custom width: size by `width` and let siblings shrink so two 50% blocks plus a gap still fit the row.
        flex: hasCustomWidth ? "0 1 auto" : "1 1 0",
        minWidth: 0,
        ...customStyle,
      }}
    >
      {bg ? (
        <picture style={{ position: "absolute", inset: 0, width: "100%", height: "100%", zIndex: 0, borderRadius: "inherit", overflow: "hidden" }}>
          {mobileBg && <source media="(max-width: 767px)" srcSet={mobileBg} />}
          {tabletBg && <source media="(max-width: 1023px)" srcSet={tabletBg} />}
          <img
            className="bsb-hero-bg"
            src={desktopBg || tabletBg || mobileBg || ""}
            alt=""
            {...(bgResp?.srcSet ? { srcSet: bgResp.srcSet, sizes: bgResp.sizes } : null)}
            {...imgLoading(claimLcp(ctx))}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: (props.background?.imageSize as CSSProperties["objectFit"]) || "cover",
              objectPosition: props.background?.imagePosition || "center center",
              zIndex: 0,
              borderRadius: "inherit",
            }}
          />
        </picture>
      ) : null}
      {bgVideo ? (
        <BgVideo
          src={bgVideo}
          poster={videoPoster ?? undefined}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: (props.background?.videoFit as CSSProperties["objectFit"]) || "cover",
            objectPosition: props.background?.videoPosition || "center center",
            zIndex: 0,
            borderRadius: "inherit",
          }}
        />
      ) : null}
      <Slot
        style={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: (props.direction ? `var(--b-dir, ${props.direction})` : "var(--b-dir, column)") as CSSProperties["flexDirection"],
          ...slotBasisVars(props),
          alignItems: (props.alignCrossAxis ? `var(--b-ai, ${props.alignCrossAxis})` : "var(--b-ai, stretch)") as CSSProperties["alignItems"],
          justifyContent: (props.alignMainAxis ? `var(--b-jc, ${props.alignMainAxis})` : "var(--b-jc, flex-start)") as CSSProperties["justifyContent"],
          gap: props.gap ? `var(--b-gap, ${props.gap})` : (props.responsive?.tablet?.gap || props.responsive?.mobile?.gap ? "var(--b-gap)" : undefined),
          rowGap: props.rowGap ? `var(--b-rg, ${props.rowGap})` : (props.responsive?.tablet?.rowGap || props.responsive?.mobile?.rowGap ? "var(--b-rg)" : undefined),
          columnGap: props.colGap ? `var(--b-cg, ${props.colGap})` : (props.responsive?.tablet?.colGap || props.responsive?.mobile?.colGap ? "var(--b-cg)" : undefined),
          flexWrap: (props.flexWrap ? `var(--b-wrap, ${props.flexWrap})` : "var(--b-wrap, nowrap)") as CSSProperties["flexWrap"],
        }}
      >
        {children}
      </Slot>
    </div>
  );
}

/* ----------------------------- content ---------------------------- */

export function HeadingView({ props }: A<R.HeadingProps>): ReactNode {
  const Tag = (props.level || "h2") as ElementType;
  const customStyle = bricksElementStyle(props, { defaultWidth: "100%", defaultDisplay: "block" });

  const textDesktop = props.text;
  const textTablet = props.responsive?.tablet?.text;
  const textMobile = props.responsive?.mobile?.text;

  const hasCustomFontSize = Boolean(props.typography?.fontSize);

  const content = (
    <Tag
      className={cx(
        "bsb-heading",
        !hasCustomFontSize && (props.size === "auto" || !props.size ? `bsb-lvl-${props.level}` : `bsb-h-${props.size}`)
      )}
    >
      {textMobile ? <span className="brx-only-mobile">{textMobile}</span> : null}
      {textTablet ? <span className="brx-only-tablet">{textTablet}</span> : null}
      <span className={cx(textMobile && "brx-hide-mobile", textTablet && "brx-hide-tablet")}>
        {textDesktop}
      </span>
    </Tag>
  );

  return (
    <div
      className={cx("bsb brxe-heading brx-resp", `bsb-align-${props.align}`)}
      style={customStyle}
    >
      {props.link ? <a href={props.link} style={{ color: "inherit", textDecoration: "none" }}>{content}</a> : content}
    </div>
  );
}

export function TextView({ props }: A<R.TextProps>): ReactNode {
  const customStyle = bricksElementStyle(props, { defaultWidth: "100%", defaultDisplay: "block" });

  const textDesktop = props.text;
  const textTablet = props.responsive?.tablet?.text;
  const textMobile = props.responsive?.mobile?.text;

  const renderContent = (raw: string) => {
    const html = parseMarkdownToHtml(raw);
    return <div className="bsb-text" dangerouslySetInnerHTML={{ __html: html }} />;
  };

  const content = (
    <>
      {textMobile ? <div className="brx-only-mobile">{renderContent(textMobile)}</div> : null}
      {textTablet ? <div className="brx-only-tablet">{renderContent(textTablet)}</div> : null}
      <div className={cx(textMobile && "brx-hide-mobile", textTablet && "brx-hide-tablet")}>
        {renderContent(textDesktop || "")}
      </div>
    </>
  );

  return (
    <div
      className={cx("bsb brxe-text-basic brx-resp", `bsb-align-${props.align}`, props.muted && "bsb-muted")}
      style={customStyle}
    >
      {props.link ? <a href={props.link} style={{ color: "inherit", textDecoration: "none" }}>{content}</a> : content}
    </div>
  );
}

const RATIO_CLASS = { auto: "", square: "bsb-ratio-square", "4-3": "bsb-ratio-4-3", "16-9": "bsb-ratio-16-9" } as const;

export function ImageView({ props, ctx }: A<R.ImageProps>): ReactNode {
  const customStyle = bricksElementStyle(props, { defaultWidth: "100%", defaultDisplay: "block" });

  const desktopDetail = props.mediaId ? ctx?.mediaDetails?.(props.mediaId) : null;
  const desktopSrc = desktopDetail?.url ?? (props.mediaId ? mediaSrc(ctx, props.mediaId) : props.imageUrl);

  const tabletMediaId = props.responsive?.tablet?.mediaId;
  const tabletImageUrl = props.responsive?.tablet?.imageUrl;
  const tabletDetail = tabletMediaId ? ctx?.mediaDetails?.(tabletMediaId) : null;
  const tabletSrc = tabletDetail?.url ?? (tabletMediaId ? mediaSrc(ctx, tabletMediaId) : tabletImageUrl);

  const mobileMediaId = props.responsive?.mobile?.mediaId;
  const mobileImageUrl = props.responsive?.mobile?.imageUrl;
  const mobileDetail = mobileMediaId ? ctx?.mediaDetails?.(mobileMediaId) : null;
  const mobileSrc = mobileDetail?.url ?? (mobileMediaId ? mediaSrc(ctx, mobileMediaId) : mobileImageUrl);

  const activeSrc = desktopSrc || tabletSrc || mobileSrc;
  if (!activeSrc) return <div className="bsb bsb-placeholder">Add an image</div>;

  const detail = desktopDetail || tabletDetail || mobileDetail;
  const imgInfo = mediaImage(
    detail
      ? {
          url: activeSrc,
          width: detail.width,
          height: detail.height,
          focalX: detail.focalX,
          focalY: detail.focalY,
          dominantColor: detail.dominantColor,
          derivatives: detail.derivatives,
        }
      : { url: activeSrc },
    { sizes: "(max-width: 768px) 100vw, 800px" },
  );

  const altText = props.alt && props.alt.trim().length > 0 ? props.alt : (detail?.alt ?? "");
  const objectPos = props.objectPosition || imgInfo.objectPosition || "center center";
  const objectFitStyle = props.objectFit || "cover";

  const rawImg = (
    <img
      className={cx("bsb-img", RATIO_CLASS[(props.ratio || "auto") as keyof typeof RATIO_CLASS], props.rounded && "bsb-rounded")}
      src={desktopSrc || activeSrc}
      srcSet={desktopSrc ? imgInfo.srcSet : undefined}
      sizes={imgInfo.sizes}
      alt={altText}
      loading="lazy"
      style={{
        objectFit: objectFitStyle as CSSProperties["objectFit"],
        objectPosition: objectPos,
        backgroundColor: imgInfo.dominantColor ?? undefined,
        width: "100%",
        height: "100%",
        borderRadius: "inherit",
      }}
    />
  );

  const renderedImage = (tabletSrc || mobileSrc) ? (
    <picture>
      {mobileSrc ? <source media="(max-width: 767px)" srcSet={mobileSrc} /> : null}
      {tabletSrc ? <source media="(max-width: 1023px)" srcSet={tabletSrc} /> : null}
      {rawImg}
    </picture>
  ) : rawImg;

  return (
    <figure className="bsb brxe-image brx-resp" style={{ margin: 0, ...customStyle }}>
      {props.href ? <a href={props.href}>{renderedImage}</a> : renderedImage}
    </figure>
  );
}

function embed(url: string): { kind: "iframe" | "video"; src: string } | null {
  try {
    const u = new URL(url);
    if (/(^|\.)youtube\.com$/.test(u.hostname) && u.searchParams.get("v"))
      return { kind: "iframe", src: `https://www.youtube-nocookie.com/embed/${u.searchParams.get("v")}` };
    if (u.hostname === "youtu.be") return { kind: "iframe", src: `https://youtube-nocookie.com/embed${u.pathname}` };
    if (u.hostname === "vimeo.com") return { kind: "iframe", src: `https://player.vimeo.com/video${u.pathname}` };
    if (/\.(mp4|webm)$/i.test(u.pathname)) return { kind: "video", src: url };
  } catch {
    /* invalid URL: placeholder below */
  }
  return null;
}

export function VideoView({ props, ctx }: A<R.VideoProps>): ReactNode {
  const customStyle = bricksElementStyle(props, { defaultWidth: "100%", defaultDisplay: "block" });

  const desktopMediaUrl = props.mediaId ? mediaSrc(ctx, props.mediaId) : null;
  const tabletMediaUrl = props.responsive?.tablet?.mediaId ? mediaSrc(ctx, props.responsive.tablet.mediaId) : null;
  const mobileMediaUrl = props.responsive?.mobile?.mediaId ? mediaSrc(ctx, props.responsive.mobile.mediaId) : null;

  const posterUrl = props.posterMediaId ? mediaSrc(ctx, props.posterMediaId) : null;
  const showControls = props.controls !== false && !props.autoplayLoop;
  const videoFit = (props.objectFit || "cover") as CSSProperties["objectFit"];
  const videoPos = props.objectPosition || "center center";

  if (props.mediaId || tabletMediaUrl || mobileMediaUrl) {
    const activeUrl = desktopMediaUrl || tabletMediaUrl || mobileMediaUrl;
    if (!activeUrl) {
      return <div className="bsb bsb-placeholder">Video not found</div>;
    }
    return (
      <div className="bsb brxe-video brx-resp" style={customStyle}>
        <video
          className="bsb-video"
          poster={posterUrl ?? undefined}
          controls={showControls}
          autoPlay={props.autoplayLoop}
          loop={props.autoplayLoop}
          muted={props.autoplayLoop}
          playsInline
          preload="none"
          style={{ width: "100%", height: "100%", objectFit: videoFit, objectPosition: videoPos, borderRadius: "inherit" }}
        >
          {mobileMediaUrl ? <source media="(max-width: 767px)" src={mobileMediaUrl} /> : null}
          {tabletMediaUrl ? <source media="(max-width: 1023px)" src={tabletMediaUrl} /> : null}
          {desktopMediaUrl ? <source src={desktopMediaUrl} /> : null}
        </video>
      </div>
    );
  }

  const desktopUrl = props.url;
  const tabletUrl = props.responsive?.tablet?.videoUrl;
  const mobileUrl = props.responsive?.mobile?.videoUrl;
  const activeExternalUrl = desktopUrl || tabletUrl || mobileUrl;

  const e = activeExternalUrl ? embed(activeExternalUrl) : null;
  if (!e) return <div className="bsb bsb-placeholder">Add a video from media or a link</div>;

  const renderSingleVideoOrIframe = (embedData: { kind: "iframe" | "video"; src: string }) => {
    return embedData.kind === "video" ? (
      <video
        className="bsb-video"
        src={embedData.src}
        poster={posterUrl ?? undefined}
        controls={showControls}
        autoPlay={props.autoplayLoop}
        loop={props.autoplayLoop}
        muted={props.autoplayLoop}
        preload="none"
        playsInline
        style={{ width: "100%", height: "100%", objectFit: videoFit, objectPosition: videoPos, borderRadius: "inherit" }}
      />
    ) : (
      <iframe
        className="bsb-video"
        src={embedData.src}
        loading="lazy"
        allowFullScreen
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        style={{ width: "100%", height: "100%", objectFit: videoFit, border: 0, borderRadius: "inherit" }}
      />
    );
  };

  const mobileEmbed = mobileUrl ? embed(mobileUrl) : null;
  const tabletEmbed = tabletUrl ? embed(tabletUrl) : null;

  return (
    <div className="bsb brxe-video brx-resp" style={customStyle}>
      {mobileEmbed ? (
        <div className="brx-only-mobile" style={{ width: "100%", height: "100%" }}>
          {renderSingleVideoOrIframe(mobileEmbed)}
        </div>
      ) : null}
      {tabletEmbed ? (
        <div className="brx-only-tablet" style={{ width: "100%", height: "100%" }}>
          {renderSingleVideoOrIframe(tabletEmbed)}
        </div>
      ) : null}
      <div className={cx(mobileEmbed && "brx-hide-mobile", tabletEmbed && "brx-hide-tablet")} style={{ width: "100%", height: "100%" }}>
        {renderSingleVideoOrIframe(e)}
      </div>
    </div>
  );
}

export function ButtonView({ props }: A<R.ButtonProps>): ReactNode {
  const styleType = props.styleType || "theme";
  const customStyle = bricksElementStyle(props, { defaultDisplay: "inline-flex" });

  const labelDesktop = props.text || props.label || "Click here";
  const labelTablet = props.responsive?.tablet?.text;
  const labelMobile = props.responsive?.mobile?.text;

  const href = props.link || props.href || "#";
  const target = props.newTab ? "_blank" : undefined;
  const relAttr = props.newTab ? "noopener noreferrer" : undefined;

  const iconName = props.icon && props.icon !== "none" ? props.icon : undefined;
  const iconPos = props.iconPosition || "left";
  const iconGap = props.iconGap || "8px";

  // CSS variables for custom colors & icon gap
  const btnStyle: CSSProperties & Record<string, string | undefined> = {};
  if (props.buttonColor) btnStyle["--brx-btn-bg"] = props.buttonColor;
  if (props.textColor) btnStyle["--brx-btn-tc"] = props.textColor;
  if (props.hoverButtonColor) btnStyle["--brx-btn-hbg"] = props.hoverButtonColor;
  if (props.hoverTextColor) btnStyle["--brx-btn-htc"] = props.hoverTextColor;
  if (iconGap) btnStyle["--brx-btn-gap"] = iconGap;

  const iconEl = iconName ? (
    <span className="brxe-button-icon" style={{ display: "inline-flex", alignItems: "center", flexShrink: 0 }}>
      <SvgIcon name={iconName} px={18} />
    </span>
  ) : null;

  const labelEl = (
    <span className="brxe-button-text">
      {labelMobile ? <span className="brx-only-mobile">{labelMobile}</span> : null}
      {labelTablet ? <span className="brx-only-tablet">{labelTablet}</span> : null}
      <span className={cx(labelMobile && "brx-hide-mobile", labelTablet && "brx-hide-tablet")}>
        {labelDesktop}
      </span>
    </span>
  );

  return (
    <div
      className={cx("bsb brxe-button brx-resp", `bsb-align-${props.align}`, props.fullWidth && "brx-btn-full")}
      style={customStyle}
    >
      <a
        href={href}
        target={target}
        rel={relAttr}
        className={cx(
          "bsb-btn",
          styleType === "theme" && `bsb-btn-${props.variant || "primary"}`,
          styleType === "normal" && "brx-btn-normal",
          styleType === "line" && "brx-btn-line",
          styleType === "text" && "brx-btn-text",
          props.size !== "auto" && `bsb-btn-${props.size}`,
          props.fullWidth && "bsb-btn-block"
        )}
        style={btnStyle}
      >
        {iconPos === "left" && iconEl}
        {labelEl}
        {iconPos === "right" && iconEl}
      </a>
    </div>
  );
}

/* ------------------- store chrome and page cores ------------------- */

function NavLink({ ctx, href, className, children }: { ctx: RenderContext | undefined; href: string; className?: string; children: ReactNode }) {
  if (ctx?.renderLink) return <>{ctx.renderLink({ href, ...(className ? { className } : {}), children })}</>;
  return (
    <a href={href} className={className} {...(isExternal(href) ? { rel: "noopener noreferrer" } : {})}>
      {children}
    </a>
  );
}

const SearchIcon = (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const CartIcon = (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="9" cy="21" r="1" />
    <circle cx="20" cy="21" r="1" />
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
  </svg>
);

export function SiteHeaderView({ props, ctx }: A<R.SiteHeaderProps>): ReactNode {
  const name = props.logoText || ctx?.storeName || "Your store";
  const customLogoUrl = mediaSrc(ctx, props.logoMediaId);
  const logoUrl = customLogoUrl || (!props.logoText ? ctx?.logoUrl : null);

  const d = ctx?.blockId ? ctx.data?.[ctx.blockId] : undefined;
  const menuData = d && d.kind === "menu" ? d.items : undefined;
  const hasResolvedMenu = Array.isArray(menuData) && menuData.length > 0;

  return (
    <header className={cx("bsb bs-block-site-header bsb-site-header", `bsb-tone-${props.tone}`, props.sticky && "bsb-sticky", props.layout === "center" && "bsb-site-header-center", `bsb-hl-${props.layout}`)}>
      <div className="bsb-w bsb-w-wide bsb-site-header-row">
        {hasResolvedMenu ? (
          <MobileMenu>
            <div>
              <nav className="bsb-mobile-nav" aria-label="Mobile Navigation">
                <ul className="bsb-mobile-list">
                  {menuData.map((item) => (
                    <li key={item.id} className="bsb-mobile-item">
                      {item.children && item.children.length > 0 ? (
                        <details className="bsb-mobile-accordion">
                          <summary className="bsb-mobile-accordion-header">
                            <span>{item.label}</span>
                            <svg className="bsb-chevron" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <path d="m6 9 6 6 6-6" />
                            </svg>
                          </summary>
                          <div className="bsb-mobile-sublist">
                            {item.children.map((child) => (
                              <div key={child.id} className="bsb-mobile-subitem">
                                {child.children && child.children.length > 0 ? (
                                  <details className="bsb-mobile-accordion bsb-mobile-subaccordion">
                                    <summary className="bsb-mobile-accordion-header">
                                      <span>{child.label}</span>
                                      <svg className="bsb-chevron" viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                        <path d="m6 9 6 6 6-6" />
                                      </svg>
                                    </summary>
                                    <div className="bsb-mobile-subsublist">
                                      {child.children.map((sub) => (
                                        <NavLink key={sub.id} ctx={ctx} href={sub.url} className="bsb-mobile-link">
                                          {sub.label}
                                        </NavLink>
                                      ))}
                                    </div>
                                  </details>
                                ) : (
                                  <NavLink ctx={ctx} href={child.url} className="bsb-mobile-link">
                                    {child.label}
                                  </NavLink>
                                )}
                              </div>
                            ))}
                          </div>
                        </details>
                      ) : (
                        <NavLink ctx={ctx} href={item.url} className="bsb-mobile-link">
                          {item.label}
                        </NavLink>
                      )}
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
          </MobileMenu>
        ) : null}
        <NavLink ctx={ctx} href="/" className="bsb-site-logo">
          {logoUrl ? <img className="bsb-logo-img" {...logoAttrs(ctx, props.logoMediaId, logoUrl)} alt={name} style={logoSizeStyle(props.logoSize)} /> : <span>{name}</span>}
        </NavLink>
        <nav className="bsb-site-nav" aria-label="Main">
          {hasResolvedMenu ? <NavDropdownCloser /> : null}
          {hasResolvedMenu ? (
            menuData.map((item) =>
              item.children && item.children.length > 0 ? (
                <div key={item.id} className={`bsb-nav-item bsb-has-dropdown${item.children.some((c) => c.children && c.children.length > 0) ? " bsb-has-mega" : ""}`}>
                  <NavLink ctx={ctx} href={item.url} className="bsb-nav-link">
                    <span>{item.label}</span>
                    <svg className="bsb-chevron" viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="m6 9 6 6 6-6" />
                    </svg>
                  </NavLink>
                  <div className="bsb-dropdown-menu">
                    <ul className="bsb-dropdown-list">
                      {item.children.map((child) => (
                        <li key={child.id} className="bsb-dropdown-item">
                          <NavLink ctx={ctx} href={child.url} className="bsb-dropdown-link">
                            {child.label}
                          </NavLink>
                          {child.children && child.children.length > 0 ? (
                            <ul className="bsb-dropdown-sublist">
                              {child.children.map((sub) => (
                                <li key={sub.id} className="bsb-dropdown-subitem">
                                  <NavLink ctx={ctx} href={sub.url} className="bsb-dropdown-sublink">
                                    {sub.label}
                                  </NavLink>
                                </li>
                              ))}
                            </ul>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ) : (
                <NavLink key={item.id} ctx={ctx} href={item.url} className="bsb-nav-link">
                  {item.label}
                </NavLink>
              ),
            )
          ) : (
            props.links.map((l, i) => (
              <NavLink key={`${l.href}-${i}`} ctx={ctx} href={l.href}>
                {l.label}
              </NavLink>
            ))
          )}
        </nav>
        <div className="bsb-site-actions">
          {props.showSearch ? (
            <NavLink ctx={ctx} href="/search" className="bsb-site-icon bsb-site-search">
              <span className="bsb-sr">Search</span>
              {SearchIcon}
            </NavLink>
          ) : null}
          {props.showCart ? (
            ctx?.renderCart ? (
              ctx.renderCart()
            ) : (
              <NavLink ctx={ctx} href="/cart" className="bsb-site-icon">
                <span className="bsb-sr">Cart</span>
                {CartIcon}
              </NavLink>
            )
          ) : null}
          {props.showAccount ? (
            ctx?.renderAccount ? (
              ctx.renderAccount()
            ) : (
              <NavLink ctx={ctx} href="/account" className="bsb-site-icon">
                <span className="bsb-sr">Account</span>
                {UserIcon}
              </NavLink>
            )
          ) : ctx?.renderAccount ? (
            // Phones always show the account pill; a header that turned the account icon off keeps it off on larger screens.
            <span className="bsb-phone-only">{ctx.renderAccount()}</span>
          ) : null}
        </div>
      </div>
    </header>
  );
}

const SOCIAL_PATHS: Record<R.SocialNetwork, ReactNode> = {
  instagram: (
    <>
      <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
    </>
  ),
  facebook: <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />,
  x: <path d="M4 4l16 16M20 4L4 20" />,
  youtube: (
    <>
      <path d="M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.55 49.55 0 0 1-16.2 0A2 2 0 0 1 2.5 17" />
      <path d="m10 15 5-3-5-3z" />
    </>
  ),
  linkedin: (
    <>
      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
      <rect width="4" height="12" x="2" y="9" />
      <circle cx="4" cy="4" r="2" />
    </>
  ),
  whatsapp: <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />,
  email: (
    <>
      <rect width="20" height="16" x="2" y="4" rx="2" />
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </>
  ),
  website: (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </>
  ),
};
/** Solid brand glyphs (24px grid) for the footer's "plain" and "filled" styles; "outline" and the networks without a brand mark keep the line icons above. */
const SOCIAL_FILLED: Partial<Record<R.SocialNetwork, string>> = {
  facebook: "M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z",
  youtube: "M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z",
  x: "M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z",
  instagram: "M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z",
  linkedin: "M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z",
  whatsapp: "M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z",
};
const SOCIAL_LABELS: Record<R.SocialNetwork, string> = { instagram: "Instagram", facebook: "Facebook", x: "X", youtube: "YouTube", linkedin: "LinkedIn", whatsapp: "WhatsApp", email: "Email", website: "Website" };

function FooterSocials({ props }: { props: R.SiteFooterProps }): ReactNode {
  const socials = props.socials.filter((s) => s.url.replace(/^https?:\/\//i, "").trim() !== "");
  if (socials.length === 0) return null;
  const size = props.socialSize;
  return (
    <ul className={cx("bsb-socials", `bsb-socials-${props.socialStyle}`, `bsb-socials-c-${props.socialColor}`)} style={{ "--soc": `${size}px` } as CSSProperties}>
      {socials.map((s, i) => (
        <li key={`${s.network}-${i}`}>
          <a href={s.url} className="bsb-social" aria-label={SOCIAL_LABELS[s.network]} {...(isExternal(s.url) ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
            {props.socialStyle !== "outline" && SOCIAL_FILLED[s.network] ? (
              <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
                <path d={SOCIAL_FILLED[s.network]} />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                {SOCIAL_PATHS[s.network]}
              </svg>
            )}
          </a>
        </li>
      ))}
    </ul>
  );
}

export function SiteFooterView({ props, ctx }: A<R.SiteFooterProps>): ReactNode {
  const name = props.logoText || ctx?.storeName || "Your store";
  const logoUrl = mediaSrc(ctx, props.logoMediaId);
  const year = new Date().getFullYear();

  const d = ctx?.blockId ? ctx.data?.[ctx.blockId] : undefined;
  const footerMenus = d && d.kind === "footer-menus" ? d.columns : undefined;

  const columns = props.columns.map((c, i) => {
    const menuCol = footerMenus ? footerMenus[i] : null;
    const links = menuCol && menuCol.length > 0 ? menuCol.map((item) => ({ label: item.label, href: item.url })) : c.links;
    return { key: `${c.title}-${i}`, title: c.title, links };
  });
  const linkList = (links: Array<{ label: string; href: string }>) => (
    <ul className="bsb-site-footer-links">
      {links.map((l, j) => (
        <li key={`${l.href}-${j}`}>
          <NavLink ctx={ctx} href={l.href}>
            {l.label}
          </NavLink>
        </li>
      ))}
    </ul>
  );
  const brand = (
    <div className="bsb-site-footer-brand">
      {logoUrl ? <img className="bsb-logo-img" {...logoAttrs(ctx, props.logoMediaId, logoUrl)} alt={name} style={logoSizeStyle(props.logoSize)} /> : <p className="bsb-heading bsb-h-sm">{name}</p>}
      {props.about ? <p className="bsb-muted" style={{ marginTop: "0.5rem", fontSize: "0.875rem" }}>{props.about}</p> : null}
      <FooterSocials props={props} />
    </div>
  );
  // Wide screens show every column open. With the accordion on, phones get the same links as one folded section per
  // column (a second copy in the markup, swapped by CSS), so a long menu never pushes the page far down.
  const columnBlocks = columns.map((c) => (
    <div key={c.key} className="bsb-site-footer-col">
      <div className="bsb-site-footer-open">
        <p className="bsb-site-footer-title">{c.title}</p>
        {linkList(c.links)}
      </div>
      {props.mobileAccordion ? (
        <details className="bsb-site-footer-fold">
          <summary className="bsb-site-footer-title">
            <span>{c.title}</span>
            <svg className="bsb-chevron" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="m6 9 6 6 6-6" />
            </svg>
          </summary>
          {linkList(c.links)}
        </details>
      ) : null}
    </div>
  ));
  const newsletter = props.showNewsletter ? (
    <div className="bsb-site-footer-news">
      <p className="bsb-site-footer-title">{props.newsletterTitle}</p>
      <p className="bsb-muted" style={{ fontSize: "0.8125rem", margin: "0.5rem 0 0.75rem" }}>{props.newsletterText}</p>
      <NewsletterForm placeholder="Your email" style={{ margin: 0 }} />
    </div>
  ) : null;

  return (
    <footer
      className={cx("bsb bs-block-site-footer", `bsb-tone-${props.tone}`, `bsb-fl-${props.layout}`, props.mobileAccordion && "bsb-fl-accordion")}
      style={footerColors(props.bgColor, props.textColor)}
    >
      <div className="bsb-w bsb-w-wide bsb-site-footer-w bsb-py-lg">
        {props.layout === "columns" ? (
          <div className="bsb-site-footer-grid">
            {brand}
            {columnBlocks}
            {newsletter}
          </div>
        ) : (
          <div className="bsb-site-footer-stack">
            {brand}
            <div className="bsb-site-footer-row">{columnBlocks}</div>
            {newsletter}
          </div>
        )}
        <div className="bsb-site-footer-bottom bsb-muted">
          <p>{props.copyright || `© ${year} ${name}. All rights reserved.`}</p>
          {/* Fixed platform credit: rendered here, not from block props, so a merchant cannot edit or hide it (ADR-010). */}
          <a className="bsb-powered" href="https://bcom.si" target="_blank" rel="noopener">
            Powered by BS Commerce
          </a>
        </div>
      </div>
    </footer>
  );
}

/** Footer colours picked in the editor: the text colour also feeds the muted text, titles and divider lines, so a dark background with light text reads everywhere. */
function footerColors(bg: string | undefined, fg: string | undefined): CSSProperties | undefined {
  if (!bg && !fg) return undefined;
  const base = bg ?? "transparent";
  return {
    ...(bg ? { background: bg } : null),
    ...(fg
      ? {
          color: fg,
          "--t": fg,
          "--m": `color-mix(in srgb, ${fg} 75%, ${base})`,
          "--b": `color-mix(in srgb, ${fg} 25%, ${base})`,
        }
      : null),
  } as CSSProperties;
}

function Placeholder({ label, hint, children }: { label: string; hint: string; children: ReactNode }) {
  return (
    <section className="bsb bs-block-placeholder bsb-py-lg">
      <div className="bsb-w bsb-w-wide">
        {children}
        <p className="bsb-muted bsb-align-center" style={{ fontSize: "0.75rem", marginTop: "1rem" }}>
          <strong>{label}</strong> · {hint}
        </p>
      </div>
    </section>
  );
}

export function ProductCustomFieldView({ props, ctx }: A<R.ProductCustomFieldProps>): ReactNode {
  if (ctx?.renderProductCustomField) return <>{ctx.renderProductCustomField(props as ProductCustomFieldOptions)}</>;
  // Editor and theme previews: a labelled sample, since the real value comes from each product.
  return (
    <Placeholder
      label={props.fieldKey ? `Custom field: ${props.fieldKey}` : "Custom field"}
      hint={props.fieldKey ? "Each product shows its own value here. Nothing is drawn when a product has no value." : "Enter the key of one of your custom fields (Settings > Custom fields)."}
    >
      <div className="bsb-ph-box" style={{ height: 64 }}>
        <span>{props.showLabel ? `${props.label || props.fieldKey || "Field name"}: ` : ""}Value from the product</span>
      </div>
    </Placeholder>
  );
}

export function ProductDetailView({ props, ctx }: A<R.ProductDetailProps>): ReactNode {
  if (ctx?.renderProductDetail) return <>{ctx.renderProductDetail(props as ProductDetailOptions)}</>;
  return <ProductDetailSample props={props} ctx={ctx} />;
}

export function CollectionListingView({ props, ctx }: A<R.CollectionListingProps>): ReactNode {
  if (ctx?.renderCollectionListing) return <>{ctx.renderCollectionListing(props as CollectionListingOptions)}</>;
  const cards = Array.from({ length: Number(props.columns) * 2 }, (_, i) => (
    <div key={i}>
      <div className="bsb-ph-box" style={{ aspectRatio: "4 / 5" }} aria-hidden="true" />
      <p style={{ margin: "0.5rem 0 0.15rem", fontWeight: 600 }}>Sample product {i + 1}</p>
      <p className="bsb-muted" style={{ margin: 0 }}>{money(49900 + i * 10000)}</p>
    </div>
  ));
  return (
    <Placeholder label="Collection page" hint="The collection's own products are listed here.">
      <h1 className="bsb-heading bsb-h-lg">Collection name</h1>
      {props.showDescription ? <p className="bsb-muted" style={{ margin: "0.5rem 0 1rem" }}>The collection description appears here.</p> : null}
      {props.showFilters ? <p className="bsb-muted" style={{ fontSize: "0.8125rem", margin: "0 0 1rem" }}>Sort · In stock only</p> : null}
      <div className="bsb-grid bsb-gap-md" style={{ ["--cols" as string]: props.columns, ["--cols-m" as string]: 2 }}>{cards}</div>
    </Placeholder>
  );
}

export function CollectionProductsView({ props, ctx }: A<R.CollectionProductsProps>): ReactNode {
  if (ctx?.renderCollectionListing) return <>{ctx.renderCollectionListing({ ...props, bare: true } as CollectionListingOptions)}</>;
  const cards = Array.from({ length: Number(props.columns) * 2 }, (_, i) => (
    <div key={i}>
      <div className="bsb-ph-box" style={{ aspectRatio: "4 / 5" }} aria-hidden="true" />
      <p style={{ margin: "0.5rem 0 0.15rem", fontWeight: 600 }}>Sample product {i + 1}</p>
      <p className="bsb-muted" style={{ margin: 0 }}>{money(49900 + i * 10000)}</p>
    </div>
  ));
  return (
    <section className={cx("bsb bs-block-collection-products bsb-lay", `bsb-tone-${props.tone}`)} style={layoutStyle(props.box)}>
      <div className={cx("bsb-w", `bsb-w-${props.width}`)}>
        {props.showTitle ? <h1 className="bsb-heading bsb-h-lg">Collection name</h1> : null}
        {props.showDescription ? <p className="bsb-muted" style={{ margin: "0.5rem 0 1rem" }}>The collection's short description appears here.</p> : null}
        {props.showFilters ? <p className="bsb-muted" style={{ fontSize: "0.8125rem", margin: "0 0 1rem" }}>Sort · In stock only</p> : null}
        <div className="bsb-grid bsb-gap-md" style={{ ["--cols" as string]: props.columns, ["--cols-m" as string]: props.columnsMobile }}>{cards}</div>
        {props.showLongDescription ? <p className="bsb-muted" style={{ marginTop: "1.5rem" }}>The collection's long description appears here, below the products.</p> : null}
        <p className="bsb-muted bsb-align-center" style={{ fontSize: "0.75rem", marginTop: "1rem" }}>
          <strong>Collection products</strong> · This collection's own products are listed here on the storefront.
        </p>
      </div>
    </section>
  );
}

export function CartContentsView({ props, ctx }: A<R.CartContentsProps>): ReactNode {
  if (ctx?.renderCartContents) return <>{ctx.renderCartContents(props as CartContentsOptions)}</>;
  const items = (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
      {[1, 2].map((n) => (
        <div key={n} style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
          <div className="bsb-ph-box" style={{ width: 88, height: 88, flex: "none" }} aria-hidden="true" />
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, fontWeight: 600 }}>Product {n}</p>
            <p className="bsb-muted" style={{ margin: "0.15rem 0 0" }}>Qty 1 · {money(129900)}</p>
          </div>
        </div>
      ))}
    </div>
  );
  const summary = (
    <div className="bsb-ph-box" style={{ display: "block", padding: "1.25rem", textAlign: "left" }}>
      <p style={{ margin: 0, fontWeight: 700 }}>Order summary</p>
      <p className="bsb-muted" style={{ margin: "0.5rem 0" }}>Subtotal {money(259800)}</p>
      {props.showDiscountCode ? <p className="bsb-muted" style={{ margin: "0.25rem 0" }}>Discount code</p> : null}
      <span className="bsb-btn bsb-btn-primary bsb-btn-lg" style={{ justifyContent: "center", marginTop: "0.75rem", display: "flex" }}>{props.checkoutLabel}</span>
      {props.showTrustPoints ? <p className="bsb-muted" style={{ fontSize: "0.75rem", marginTop: "0.75rem" }}>{props.trustPoints.join(" · ")}</p> : null}
      {props.showShippingEstimator ? <p className="bsb-muted" style={{ fontSize: "0.75rem", marginTop: "0.75rem" }}>Shipping estimator</p> : null}
    </div>
  );
  return (
    <Placeholder label="Cart page" hint="The shopper's own cart, totals and checkout button fill this area.">
      <h1 className="bsb-heading bsb-h-lg" style={{ marginBottom: "1rem" }}>{props.heading}</h1>
      <div className="bsb-ph-two">{props.summaryPosition === "left" ? <>{summary}{items}</> : <>{items}{summary}</>}</div>
    </Placeholder>
  );
}

/* ------------------------------ hero slider ----------------------------- */

const SHADE_HEIGHT = { sm: "35%", md: "55%", lg: "80%" } as const;
const BTN_VARIANT = { dark: "bsb-btn-primary", light: "bsb-btn-secondary", outline: "bsb-btn-outline" } as const;

function slideButton(label: string, href: string, variant: keyof typeof BTN_VARIANT, size: string, custom: boolean, which: "p" | "s"): ReactNode {
  if (!label) return null;
  return (
    <a
      className={cx("bsb-btn", BTN_VARIANT[variant], size !== "auto" && `bsb-btn-${size}`, custom && `bsb-hs-custom-${which}`)}
      href={href || "#"}
      rel={rel(href)}
    >
      {label}
    </a>
  );
}

export function HeroSliderView({ props, ctx }: A<R.HeroSliderProps>): ReactNode {
  const n = props.slides.length;
  const editorPreview = !ctx?.renderHeroSlider;
  const style = {
    "--hs-shade-h": SHADE_HEIGHT[props.shadeHeight],
    "--hs-shade-o": props.shade ? props.shadeStrength / 100 : 0,
    "--hs-dim": props.dim / 100,
    ...(props.primaryBg ? { "--hs-p-bg": props.primaryBg } : null),
    ...(props.primaryText ? { "--hs-p-fg": props.primaryText } : null),
    ...(props.secondaryBg ? { "--hs-s-bg": props.secondaryBg } : null),
    ...(props.secondaryText ? { "--hs-s-fg": props.secondaryText } : null),
    ...(props.mobileTextBg ? { "--hs-m-bg": props.mobileTextBg } : null),
    ...(props.mobileTextColor ? { "--hs-m-fg": props.mobileTextColor } : null),
  } as CSSProperties;
  const customP = Boolean(props.primaryBg || props.primaryText);
  const customS = Boolean(props.secondaryBg || props.secondaryText);

  const markup = (
    <div className={cx("bsb-lay", `bsb-hs-wm-${props.widthMode}`, props.widthModeTablet !== "inherit" && `bsb-hs-wmt-${props.widthModeTablet}`, props.widthModeMobile !== "inherit" && `bsb-hs-wmm-${props.widthModeMobile}`, props.slideRadius.enabled && "bsb-hs-rounded", layoutHasAspectRatio(props.box) && "bsb-hs-ar")} style={{ ...layoutStyle(props.box), ...slideRadiusStyle(props.slideRadius) }}>
    <section
      className={cx(
        "bsb bsb-hs",
        `bsb-hs-h-${props.height}`,
        `bsb-hs-layout-${props.layout}`,
        `bsb-hs-align-${props.align}`,
        `bsb-hs-pos-${props.contentPosition}`,
        `bsb-hs-w-${props.contentWidth}`,
        `bsb-hs-m-${props.mobileLayout}`,
      )}
      style={style}
      data-hs=""
      aria-roledescription="carousel"
      aria-label="Featured"
    >
      <div className="bsb-hs-track" data-hs-track="" tabIndex={n > 1 ? 0 : undefined}>
        {props.slides.map((s, i) => {
          const img = mediaSrc(ctx, s.backgroundMediaId);
          const Title = i === 0 ? props.titleTag : "h2";
          // A slide with only a picture or video gets no text panel, so there is no empty block under it on phones.
          const hasContent = Boolean(s.title || s.subtitle || s.primaryLabel || s.secondaryLabel);
          return (
            <div key={i} className="bsb-hs-slide" role="group" aria-roledescription="slide" aria-label={`${i + 1} of ${n}`} data-hs-slide="">
              <div className="bsb-hs-media">
                {img ? (
                  // The first slide is the page's largest paint: load it eagerly and early; the rest wait until they are near.
                  <img src={img} alt="" decoding="async" loading={i === 0 ? "eager" : "lazy"} {...(i === 0 ? { fetchPriority: "high" as const } : null)} />
                ) : null}
                {s.videoUrl ? (
                  <video
                    muted
                    loop
                    playsInline
                    preload="none"
                    {...(img ? { poster: img } : null)}
                    {...(editorPreview && i === 0 ? { autoPlay: true } : null)}
                    data-hs-video=""
                    aria-hidden="true"
                  >
                    <source src={s.videoUrl} />
                  </video>
                ) : null}
              </div>
              {hasContent ? <div className="bsb-hs-shade" aria-hidden="true" /> : null}
              {hasContent ? (
              <div className="bsb-hs-content">
                <div className="bsb-hs-inner">
                  {s.title || s.subtitle ? (
                    <div className="bsb-hs-text">
                      {s.title ? <Title className={cx("bsb-heading", props.titleSize === "xl" ? "bsb-lvl-h1" : "bsb-lvl-h2")}>{s.title}</Title> : null}
                      {s.subtitle ? <p className="bsb-hs-sub">{s.subtitle}</p> : null}
                    </div>
                  ) : null}
                  {s.primaryLabel || s.secondaryLabel ? (
                    <div className="bsb-hs-actions">
                      {slideButton(s.primaryLabel, s.primaryHref, props.primaryStyle, props.buttonSize, customP, "p")}
                      {slideButton(s.secondaryLabel, s.secondaryHref, props.secondaryStyle, props.buttonSize, customS, "s")}
                    </div>
                  ) : null}
                </div>
              </div>
              ) : null}
            </div>
          );
        })}
      </div>
      {n > 1 && props.arrows ? (
        <div className={cx("bsb-hs-arrows", `bsb-hs-arrow-${props.arrowStyle}`, `bsb-hs-arrow-${props.arrowSize}`, `bsb-hs-arrow-c-${props.arrowColor}`, props.arrowsOnMobile && "bsb-hs-arrows-m")}>
          <button type="button" className="bsb-hs-arrow bsb-hs-prev" data-hs-prev="" aria-label="Previous slide">
            <svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M15 5l-7 7 7 7" />
            </svg>
          </button>
          <button type="button" className="bsb-hs-arrow bsb-hs-next" data-hs-next="" aria-label="Next slide">
            <svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      ) : null}
      {n > 1 && props.dots ? (
        <div className={cx("bsb-hs-dots", props.arrows && !props.dotsDesktop && "bsb-hs-dots-mobile", `bsb-hs-dots-${props.dotsPosition}`, `bsb-hs-dot-${props.dotsStyle}`, `bsb-hs-dots-${props.dotsSize}`, `bsb-hs-dots-c-${props.dotsColor}`)} role="group" aria-label="Choose slide">
          {props.slides.map((_, i) => (
            <button key={i} type="button" className="bsb-hs-dotbtn" data-hs-dot="" aria-label={`Go to slide ${i + 1}`} {...(i === 0 ? { "aria-current": "true" as const } : null)} />
          ))}
        </div>
      ) : null}
    </section>
    </div>
  );

  const behavior = { autoplay: props.autoplay && n > 1, interval: props.interval, loop: props.loop, pauseOnHover: props.pauseOnHover };
  return ctx?.renderHeroSlider ? <>{ctx.renderHeroSlider({ options: behavior, children: markup })}</> : markup;
}

/* --------------------------- product showcase --------------------------- */

/** The logo's size per device as CSS variables (blocks.css picks the set for the screen, a smaller screen inheriting the larger one). */
function logoSizeStyle(size: LogoSize | undefined): CSSProperties {
  const out: Record<string, string> = {};
  const letter = { desktop: "d", tablet: "t", mobile: "m" } as const;
  for (const device of ["desktop", "tablet", "mobile"] as const) {
    const box = size?.[device];
    if (box?.width) out[`--lg-${letter[device]}-w`] = `${box.width}px`;
    if (box?.height) out[`--lg-${letter[device]}-h`] = `${box.height}px`;
  }
  return out as CSSProperties;
}

const UserIcon = (
  <svg viewBox="0 0 24 24" width="1.25em" height="1.25em" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21a8 8 0 0 1 16 0" />
  </svg>
);

const BAG_ICON = (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 7h12l1 13H5L6 7z" />
    <path d="M9 7a3 3 0 0 1 6 0" />
  </svg>
);

function ShowcaseCard({ p, props, ctx, eager }: { p: BlockProduct; props: R.ProductShowcaseProps; ctx: RenderContext | undefined; eager: boolean }): ReactNode {
  const imgSizes = `(max-width: 767px) ${Math.round(100 / Number(props.perViewMobile))}vw, (max-width: 1023px) ${Math.round(100 / Number(props.perViewTablet))}vw, ${Math.round(100 / Number(props.perViewDesktop))}vw`;
  const imgAttrs = responsiveImgAttrs(ctx, p.imageMediaId, imgSizes, p.imageUrl);
  const img = imgAttrs.src;
  const href = `/products/${p.slug}`;
  const hasCompare = props.showCompareAt && p.compareAtPriceMin !== undefined && p.compareAtPriceMin > p.priceMin;
  const saving = p.compareAtPriceMin && p.compareAtPriceMin > p.priceMin ? Math.round(((p.compareAtPriceMin - p.priceMin) / p.compareAtPriceMin) * 100) : 0;
  const isRange = p.priceMax !== undefined && p.priceMax > p.priceMin;
  const showFrom = props.startsFrom === "always" || (props.startsFrom === "auto" && isRange);
  const badge = props.badge === "category" ? p.categoryName : props.badge === "brand" ? p.brandName : undefined;
  const link = (children: ReactNode, className?: string, label?: string) =>
    ctx?.renderLink ? ctx.renderLink({ href, ...(className ? { className } : {}), children }) : <a href={href} className={className} {...(label ? { "aria-label": label } : null)}>{children}</a>;

  // Row 1 is the reviews (left) and the category (right); the rest of the card order (title, price) follows it.
  const ratingShown = props.cardOrder.some((c) => c.item === "rating" && c.show) && Number(p.ratingCount) > 0;
  const rows = props.cardOrder
    .filter((c) => c.show && c.item !== "rating")
    .map((c) => {
      if (c.item === "title") {
        return (
          <h3 key="title" className={cx("bsb-psc-title", `bsb-psc-title-${props.titleSize}`, props.titleLines === "2" && "bsb-psc-lines-2")}>
            {link(p.title)}
          </h3>
        );
      }
      return (
        <div key="price" className={cx("bsb-psc-price", `bsb-psc-price-${props.priceSize}`)}>
          {showFrom ? <span className="bsb-psc-from">Starts from</span> : null}
          <strong>{money(p.priceMin, ctx)}</strong>
          {hasCompare ? <s>{money(p.compareAtPriceMin as number, ctx)}</s> : null}
        </div>
      );
    });

  return (
    <article className={cx("bsb-psc-card", `bsb-psc-card-${props.cardStyle}`)}>
      <div className={cx("bsb-psc-media", `bsb-psc-ratio-${props.imageRatio}`)}>
        {link(
          <img src={img ?? PLACEHOLDER_IMAGE_URL} {...(img && imgAttrs.srcSet ? { srcSet: imgAttrs.srcSet, sizes: imgAttrs.sizes } : null)} alt={img ? (p.imageAlt ?? p.title) : ""} decoding="async" loading={eager ? "eager" : "lazy"} />,
          "bsb-psc-imglink",
          p.title,
        )}
        {props.showSaleBadge && saving > 0 ? <span className="bsb-psc-badge bsb-psc-badge-r">Save {saving}%</span> : null}
        {props.showAddToCart ? (
          <button
            type="button"
            className="bsb-psc-add"
            data-psc-add=""
            data-href={href}
            {...(p.quickAddVariantId ? { "data-variant": p.quickAddVariantId } : null)}
            aria-label={p.quickAddVariantId ? `Add ${p.title} to cart` : `Choose options for ${p.title}`}
          >
            {BAG_ICON}
          </button>
        ) : null}
      </div>
      {rows.length > 0 || badge || ratingShown ? (
        <div className="bsb-psc-info">
          {ratingShown || badge ? (
            <div className="bsb-psc-meta">
              {ratingShown ? (
                <div className="bsb-psc-rating">
                  <Stars value={Number(p.ratingAvg ?? 0)} />
                  <span className="bsb-psc-rating-num">{Number(p.ratingAvg ?? 0).toFixed(1)}</span>
                  <span className="bsb-muted">({p.ratingCount})</span>
                </div>
              ) : null}
              {badge ? <p className="bsb-psc-cat">{badge}</p> : null}
            </div>
          ) : null}
          {rows}
        </div>
      ) : null}
    </article>
  );
}

export function ProductShowcaseView({ props, ctx, }: A<R.ProductShowcaseProps>): ReactNode {
  const d = ctx?.blockId ? ctx.data?.[ctx.blockId] : undefined;
  const tabData = d && d.kind === "product-tabs" ? d.tabs : [];
  const tabs = props.tabs;
  const hasTabs = tabs.length > 1;
  const style = { "--psc-d": props.perViewDesktop, "--psc-t": props.perViewTablet, "--psc-m": props.perViewMobile } as CSSProperties;

  const markup = (
    <section className={cx("bsb bsb-psc", `bsb-tone-${props.tone}`, "bsb-lay", `bsb-psc-gap-${props.gap}`)} style={{ ...style, ...layoutStyle(props.box) }} data-psc="" aria-roledescription="carousel" aria-label={props.title || "Products"}>
      <div className="bsb-w bsb-w-wide">
        {props.title || props.subtitle || hasTabs ? (
          <div className={cx("bsb-psc-head", `bsb-psc-head-${props.headerAlign}`)}>
            <div className="bsb-psc-headtext">
              {props.title ? <h2 className="bsb-heading bsb-lvl-h2">{props.title}</h2> : null}
              {props.subtitle ? <p className="bsb-muted">{props.subtitle}</p> : null}
            </div>
            {hasTabs ? (
              <div className={cx("bsb-psc-tabs", `bsb-psc-tabs-${props.tabsStyle}`)} role="tablist" aria-label="Product groups">
                {tabs.map((t, i) => (
                  <button key={i} type="button" role="tab" className="bsb-psc-tab" data-psc-tab="" aria-selected={i === 0} tabIndex={i === 0 ? 0 : -1}>
                    {t.label || (t.source === "collection" ? t.collectionSlug : t.source === "featured" ? "Featured" : t.source === "manual" ? "Selected" : "New arrivals") || `Tab ${i + 1}`}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
        {tabs.map((_, ti) => {
          const products = tabData[ti]?.products ?? [];
          return (
            <div key={ti} className="bsb-psc-panel" data-psc-panel="" role="tabpanel" {...(ti > 0 ? { hidden: true } : null)}>
              {products.length === 0 ? (
                <p className="bsb-muted">No products to show yet.</p>
              ) : (
                <div className="bsb-psc-viewport">
                  <div className="bsb-psc-track" data-psc-track="" tabIndex={0}>
                    {products.map((p, i) => (
                      <div key={p.id} className="bsb-psc-item" data-psc-item="">
                        <ShowcaseCard p={p} props={props} ctx={ctx} eager={ti === 0 && i < props.perViewDesktop} />
                      </div>
                    ))}
                  </div>
                  {props.arrows ? (
                    <div className={cx("bsb-hs-arrows", "bsb-psc-arrows", `bsb-hs-arrow-${props.arrowStyle}`, `bsb-hs-arrow-${props.arrowSize}`, `bsb-hs-arrow-c-${props.arrowColor}`, props.arrowsOnMobile && "bsb-hs-arrows-m")}>
                      <button type="button" className="bsb-hs-arrow bsb-hs-prev" data-psc-prev="" aria-label="Previous products">
                        <svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 5l-7 7 7 7" /></svg>
                      </button>
                      <button type="button" className="bsb-hs-arrow bsb-hs-next" data-psc-next="" aria-label="Next products">
                        <svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 5l7 7-7 7" /></svg>
                      </button>
                    </div>
                  ) : null}
                </div>
              )}
              {/* The host fills the dots: how many pages there are depends on the screen. */}
              {props.dots ? <div className={cx("bsb-hs-dots", "bsb-psc-dots", props.arrows && !props.dotsDesktop && "bsb-hs-dots-mobile", `bsb-hs-dots-${props.dotsPosition}`, `bsb-hs-dot-${props.dotsStyle}`, `bsb-hs-dots-${props.dotsSize}`, `bsb-hs-dots-c-${props.dotsColor}`)} data-psc-dots="" role="group" aria-label="Choose page" /> : null}
            </div>
          );
        })}
      </div>
    </section>
  );

  const behavior = { tabs: hasTabs, autoplay: props.autoplay, interval: props.interval, loop: props.loop, pauseOnHover: props.pauseOnHover };
  return ctx?.renderProductShowcase ? <>{ctx.renderProductShowcase({ options: behavior, children: markup })}</> : markup;
}
